import { CART_UPDATED } from '@/constants/cart-events'
import { updateToCartOperation } from '@/stores/cart/operations'
import { $activeGwpPromotions } from '@/stores/gwp'
import type { Cart, CartLineInput } from '@/types/shopify'
import { isMealProgramStartDateValid } from '@/utilities/sakara'

export const validateCart = async (cart: Cart) => {
  const mealProgramUpdates = getInvalidMealProgramLineUpdates(cart)
  const giftUpdates = await getIneligibleGiftLineUpdates(cart)
  const linesToUpdate = [...mealProgramUpdates, ...giftUpdates]

  if (linesToUpdate.length > 0) {
    const newCart = await updateToCartOperation(cart.id, linesToUpdate)
    window.dispatchEvent(
      new CustomEvent(CART_UPDATED, {
        detail: newCart,
      }),
    )
  }
}

const getInvalidMealProgramLineUpdates = (cart: Cart): CartLineInput[] => {
  const cartUpdates: CartLineInput[] = []

  cart?.lines.forEach((line) => {
    const startDate = line.attributes.find(
      (attribute) => attribute.key === '_start_date',
    )?.value

    if (startDate && !isMealProgramStartDateValid(startDate)) {
      const addonsAttribute = line.attributes.find(
        (attribute) => attribute.key === '_addons',
      )?.value

      if (addonsAttribute) {
        const addonsArray = JSON.parse(addonsAttribute) as {
          id: string
          quantity: number
        }[]
        addonsArray.forEach((addon) => {
          const addonLine = cart.lines.find(
            (l) =>
              l.merchandise.id === `gid://shopify/ProductVariant/${addon.id}`,
          )
          if (addonLine) {
            cartUpdates.push({
              id: addonLine.id,
              quantity: addonLine.quantity - addon.quantity,
            })
          }
        })
      }
      cartUpdates.push({
        id: line.id,
        quantity: 0,
      })
    }
  })

  return cartUpdates
}

const getIneligibleGiftLineUpdates = async (
  cart: Cart,
): Promise<CartLineInput[]> => {
  const cartUpdates: CartLineInput[] = []

  const giftItemsInCart = cart.lines.filter(
    (line) =>
      line.attributes?.find(
        (attr) => attr.key === '_gift' && attr.value === 'true',
      ),
  )
  if (giftItemsInCart.length === 0) {
    return cartUpdates
  }

  const activeGwpPromotions = $activeGwpPromotions.get()

  for (const giftItem of giftItemsInCart) {
    const giftType = giftItem.attributes?.find(
      (attr) => attr.key === '_gift_type',
    )?.value

    if (giftType === 'product') {
      const promotion = activeGwpPromotions.find(
        (promo) =>
          promo.type === 'product' &&
          promo.giftProductVariant?.store.gid === giftItem.merchandise.id,
      )
      if (promotion) {
        const eligibleProductLinesInCart = cart.lines.filter(
          (line) =>
            promotion.eligibleProductVariants?.some(
              (variant) => variant.store.gid === line.merchandise.id,
            ),
        )
        if (eligibleProductLinesInCart.length === 0) {
          cartUpdates.push({
            id: giftItem.id,
            quantity: 0,
          })
        }
      } else {
        cartUpdates.push({
          id: giftItem.id,
          quantity: 0,
        })
      }
    } else if (giftType === 'amount') {
      const promotion = activeGwpPromotions.find(
        (promo) =>
          promo.type === 'amount' &&
          promo.tiers?.some(
            (tier) =>
              tier.giftProductVariant?.store.gid === giftItem.merchandise.id,
          ),
      )
      if (!promotion) {
        cartUpdates.push({
          id: giftItem.id,
          quantity: 0,
        })
      }
    } else {
      cartUpdates.push({
        id: giftItem.id,
        quantity: 0,
      })
    }
  }

  return cartUpdates
}
